import type { WorldAgent, WorldBuilding, WorldCompanyTree, WorldFloor } from "@uzverse/contracts";
import type { SceneNode } from "@uzverse/world-scene";
import { useEffect, useMemo, useState } from "react";
import { isBuildingNode, isLandmark } from "./bounds.js";
import { invokeWorldTool } from "./data.js";
import { useWorldT } from "./i18n.js";
import { describeTarget } from "./target.js";
import { useWorldState, worldStore } from "./store.js";
import type { WorldStore } from "./store.js";
import "./world.css";

function AgentLine({ agent }: { agent: WorldAgent }) {
  return (
    <li>
      <span>{agent.name}</span>
      {agent.title ? <span className="world-muted"> · {agent.title}</span> : null}
    </li>
  );
}

function FloorItem({ floor }: { floor: WorldFloor }) {
  const t = useWorldT();
  const level =
    floor.level < 0
      ? t("panel.basement", { level: Math.abs(floor.level) })
      : t("panel.floorLevel", { level: floor.level });
  return (
    <li className="world-floor">
      <div className="world-floor-head">
        <strong>{floor.name}</strong>
        <span className="world-muted">{level}</span>
      </div>
      <p className="world-floor-line">
        <span className="world-muted">{t("panel.lead")}: </span>
        {floor.lead ? floor.lead.name : <span className="world-muted">{t("panel.noLead")}</span>}
      </p>
      <p className="world-floor-line world-muted">
        {t("panel.workers", { count: floor.workers.length })}
      </p>
      {floor.workers.length > 0 ? (
        <ul className="world-agents">
          {floor.workers.map((w) => (
            <AgentLine key={w.id} agent={w} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

const countWorkers = (floors: readonly WorldFloor[]): number =>
  floors.reduce((n, f) => n + f.workers.length, 0);

/** The org data of a scene building: a company placement shares its id with the org building. */
function orgBuilding(
  orgs: readonly WorldCompanyTree[],
  id: string,
): { company: WorldCompanyTree["company"]; building: WorldBuilding } | null {
  for (const tree of orgs) {
    const building = tree.buildings.find((b) => b.id === id);
    if (building) return { company: tree.company, building };
  }
  return null;
}

/** How often the card re-checks whether the owner has decided. */
const PENDING_POLL_MS = 15_000;

interface PendingPlacement {
  id: string;
  kind: "place" | "move";
  x: number;
  z: number;
  reason: string;
}

/**
 * "Waiting for approval": a placement the company asked for that only the owner may allow. Shown
 * to the company's own members on their building's card; it clears once the owner decides.
 */
function PendingPlacementNote({ buildingId }: { buildingId: string }) {
  const t = useWorldT();
  const [pending, setPending] = useState<PendingPlacement | null>(null);
  useEffect(() => {
    let live = true;
    setPending(null);
    const load = () =>
      invokeWorldTool("world.listPlacementRequests", { buildingId, status: "pending" })
        .then((r) => {
          const list = (r.output as { requests?: PendingPlacement[] } | undefined)?.requests ?? [];
          if (live) setPending(list.at(-1) ?? null);
        })
        .catch(() => undefined);
    void load();
    const timer = setInterval(() => void load(), PENDING_POLL_MS);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [buildingId]);
  if (!pending) return null;
  return (
    <p className="world-pending" role="status" data-testid="world-pending-placement">
      <strong>{t("panel.pendingTitle")}</strong>{" "}
      {t(pending.kind === "move" ? "panel.pendingMove" : "panel.pendingPlace", {
        x: Math.round(pending.x),
        z: Math.round(pending.z),
      })}
      {pending.reason ? <span className="world-muted"> · {pending.reason}</span> : null}
    </p>
  );
}

function BuildingDetails({
  node,
  part,
  store,
}: {
  node: SceneNode;
  part: string | undefined;
  store: WorldStore;
}) {
  const t = useWorldT();
  const { org } = useWorldState(store);
  const landmark = isLandmark(node);
  const found = org ? orgBuilding(org.companies, node.id) : null;
  const floors = found ? [...found.building.floors].sort((x, y) => y.level - x.level) : [];
  return (
    <div className="world-details">
      <div className="world-details-head">
        <div>
          <h3>{landmark ? t("panel.landmarkTitle") : (node.label ?? t("target.unknown"))}</h3>
          {found ? (
            <p className="world-muted">
              {t("panel.company")}: {found.company.name}
            </p>
          ) : null}
        </div>
        <button type="button" className="world-close" onClick={() => store.select(null)}>
          {t("panel.close")}
        </button>
      </div>
      {part ? (
        <p className="world-target" data-testid="world-target">
          {t("target.title")}: {describeTarget(t, node, part)}
        </p>
      ) : null}
      {landmark ? (
        <p>{t("panel.landmarkText")}</p>
      ) : found ? (
        <>
          <PendingPlacementNote buildingId={node.id} />
          <p className="world-muted">
            {t("panel.floors", { count: floors.length })} ·{" "}
            {t("panel.workers", { count: countWorkers(floors) })}
          </p>
          {floors.length === 0 ? (
            <p>{t("panel.noFloors")}</p>
          ) : (
            <ul className="world-floors">
              {floors.map((f) => (
                <FloorItem key={f.id} floor={f} />
              ))}
            </ul>
          )}
        </>
      ) : (
        // Another company's building: only its public facade is shared.
        <p className="world-muted">{t("panel.publicOnly")}</p>
      )}
    </div>
  );
}

/**
 * Side panel of the Dünya page: the building list (also the way in when WebGL is unavailable) and
 * the floors, leads and workers of the selected building. Reads the shared world store.
 */
export function WorldPanel({ store = worldStore }: { store?: WorldStore }) {
  const t = useWorldT();
  const state = useWorldState(store);
  useEffect(() => store.retain(), [store]);
  const buildings = useMemo(
    () =>
      [...store.nodes.values()]
        .filter(isBuildingNode)
        .sort(
          (a, b) =>
            Number(isLandmark(b)) - Number(isLandmark(a)) ||
            (a.label ?? "").localeCompare(b.label ?? ""),
        ),
    [store, state.rev],
  );
  const selected = state.selected ? store.node(state.selected.id) : undefined;
  const companyBuildings = buildings.filter((b) => !isLandmark(b));
  const companyOf = (id: string): string | null =>
    state.org ? (orgBuilding(state.org.companies, id)?.company.name ?? null) : null;
  return (
    <aside className="world-panel" aria-label={t("panel.title")} data-testid="world-panel">
      <h2>{t("panel.title")}</h2>
      {state.status === "loading" ? <p role="status">{t("state.loading")}</p> : null}
      {state.status === "unavailable" ? (
        <div role="alert">
          <p>{t("state.unavailable")}</p>
          <button type="button" onClick={() => store.retry()}>
            {t("state.retry")}
          </button>
        </div>
      ) : null}
      {state.orgError ? <p className="world-muted">{t("state.error")}</p> : null}
      {state.status === "ready" && companyBuildings.length === 0 ? <p>{t("state.empty")}</p> : null}
      {selected ? (
        <BuildingDetails node={selected} part={state.selected?.part} store={store} />
      ) : (
        <p className="world-muted">{t("panel.hint")}</p>
      )}
      <h3 className="world-list-title">{t("panel.buildings")}</h3>
      <ul className="world-building-list">
        {buildings.map((b) => (
          <li key={b.id}>
            <button
              type="button"
              aria-pressed={b.id === state.selected?.id}
              className={b.id === state.hover?.id ? "is-hover" : undefined}
              onClick={() => store.select(b.id === state.selected?.id ? null : { id: b.id })}
              onPointerEnter={() => store.hover({ id: b.id })}
              onPointerLeave={() => store.hover(null)}
            >
              {isLandmark(b) ? t("panel.landmarkTitle") : (b.label ?? t("target.unknown"))}
              {companyOf(b.id) ? <span className="world-muted"> · {companyOf(b.id)}</span> : null}
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );
}
